import { useState } from 'react';
import type { ConsultantHoursTransactionResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { useAdminGetConsultantHoursTransactions } from '@/api/auth/admin/admin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/format';
import { formatHours } from '@/lib/hours';
import { Pencil } from 'lucide-react';
import { EditConsultantHoursTransactionDialog } from './EditConsultantHoursTransactionDialog';

interface ConsultantHoursTransactionsTableProps {
  organizationId: string;
}

const PAGE_SIZE = 10;
const COLUMN_COUNT = 7;

/**
 * Paginated, read-only table of an organization's consultant hours ledger, newest entry first.
 */
export function ConsultantHoursTransactionsTable({
  organizationId,
}: ConsultantHoursTransactionsTableProps) {
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ConsultantHoursTransactionResponse | null>(null);

  const { data, isLoading, error } = useAdminGetConsultantHoursTransactions(
    organizationId,
    { page, pageSize: PAGE_SIZE },
    { query: { enabled: !!organizationId } }
  );

  const result = data?.data;
  const items = result?.items ?? [];

  if (error) {
    return <p className="text-destructive py-2">Failed to load consultant hours history.</p>;
  }

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Hours</TableHead>
            <TableHead className="text-right">Available after</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Recorded by</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            [...Array(3)].map((_, index) => (
              <TableRow key={index}>
                {[...Array(COLUMN_COUNT)].map((__, cell) => (
                  <TableCell key={cell}>
                    <Skeleton className="h-4 w-[80px]" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} className="text-center py-6 text-muted-foreground">
                No consultant hours have been recorded for this organization.
              </TableCell>
            </TableRow>
          ) : (
            items.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{formatDateTime(entry.createdUtc)}</TableCell>
                <TableCell>
                  <Badge variant={entry.type === 'Credit' ? 'default' : 'secondary'}>
                    {entry.type}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {entry.type === 'Credit' ? '+' : '−'}
                  {formatHours(entry.hours)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {formatHours(entry.availableHoursAfter)}
                </TableCell>
                <TableCell className="max-w-[300px] truncate" title={entry.description ?? ''}>
                  {entry.description || <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>{entry.createdByEmail || entry.createdByPrincipal}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit entry"
                    onClick={() => setEditing(entry)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {result && result.totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-sm text-muted-foreground">
            Page {result.page} of {Math.ceil(result.totalCount / PAGE_SIZE)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!result.hasMore}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      )}
      <EditConsultantHoursTransactionDialog
        organizationId={organizationId}
        transaction={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}
